import { CalendarClock, ShieldCheck, Sparkles, Plug } from 'lucide-react'
import { useSelector } from 'react-redux'
import type { RootState } from '../store'
import { useGetMonitoringStatusQuery } from '../services/sessionMonitoringApi'
import { useGetProjectQuery, useGetSecurityOwnershipQuery, useGetTestingSchedulesQuery, useGetApiTokensQuery } from '../services/projectDataApi'
import ProjectSetupBanner from './ProjectSetupBanner'
import SupportCard, { type SupportStatus } from './SupportCard'

const loading: SupportStatus = { tone: 'yellow', text: 'Checking status…' }
const unavailable: SupportStatus = { tone: 'yellow', text: 'Status unavailable — open to retry' }
const setup: SupportStatus = { tone: 'red', text: 'Not configured', setup: true }
const disabled: SupportStatus = { tone: 'gray', text: 'Disabled' }

export default function TestingSetupActions({ projectId, bannerOnly = false }: { projectId: string; bannerOnly?: boolean }) {
  const user = useSelector((state: RootState) => state.auth.user)
  const monitoring = useGetMonitoringStatusQuery(projectId, { pollingInterval: 30000, skipPollingIfUnfocused: true })
  const schedules = useGetTestingSchedulesQuery(projectId, { pollingInterval: 60000, skipPollingIfUnfocused: true })
  const projectQuery = useGetProjectQuery(projectId)
  const project = projectQuery.currentData
  const tokens = useGetApiTokensQuery(user?.id ?? '', { skip: !user || bannerOnly })
  const exempt = project?.security_ownership_exempt === true
  const ownership = useGetSecurityOwnershipQuery({ projectId, targetUrl: project?.target_url ?? '', nonce: project?.security_nonce ?? '' }, {
    skip: !project?.security_nonce || exempt || !project.enabled_polish_passes.includes('security') || !project.security_probing_enabled,
    pollingInterval: 15000, skipPollingIfUnfocused: true,
  })
  const status = monitoring.currentData
  if (bannerOnly) {
    const setupComplete = Boolean(project && status?.has_sessions && schedules.currentData?.schedules.some(schedule => schedule.config.enabled)
      && (!project.enabled_polish_passes.includes('security') || exempt || ownership.currentData?.latest?.verified))
    return status?.can_edit ? <ProjectSetupBanner key={projectId} projectId={projectId} complete={setupComplete} /> : null
  }
  const healing: SupportStatus = monitoring.isError ? unavailable : !status ? loading
    : !status.configured ? setup : !status.enabled ? disabled
    : !status.has_sessions ? { tone: 'yellow', text: 'Waiting for the first session' }
    : { tone: 'white', text: 'Reviewing user sessions' }
  const savedSchedules = schedules.currentData?.schedules
  const testing: SupportStatus = schedules.isError ? unavailable : !savedSchedules ? loading
    : savedSchedules.length === 0 ? setup : !savedSchedules.some(schedule => schedule.config.enabled) ? disabled
    : { tone: 'white', text: 'Scheduled testing enabled' }
  const security: SupportStatus = projectQuery.isError ? unavailable : !project ? loading
    : !exempt && !project.security_nonce ? setup
    : !project.enabled_polish_passes.includes('security') || !project.security_probing_enabled ? disabled
    : exempt ? { tone: 'white', text: 'Security testing enabled' }
    : ownership.isError ? unavailable : !ownership.currentData ? loading
    : !ownership.currentData.latest?.verified ? setup
    : { tone: 'white', text: 'Ownership verified · security testing enabled' }
  const agent: SupportStatus = !user ? setup : tokens.isError ? unavailable : !tokens.currentData ? loading
    : tokens.currentData.tokens.length === 0 ? setup : { tone: 'white', text: 'API token configured' }
  return <div className="testing-setup-actions" aria-label="Project support">
    <SupportCard title="Self Healing" icon={Sparkles} to={`/projects/${projectId}/support/self-healing`} status={healing} />
    <SupportCard title="Continuous Testing" icon={CalendarClock} to={`/projects/${projectId}/support/continuous-testing`} status={testing} />
    <SupportCard title="Security" icon={ShieldCheck} to={`/projects/${projectId}/security`} status={security} />
    <SupportCard title="Agent Connection" icon={Plug} to={`/projects/${projectId}/support/agent-connection`} status={agent} />
  </div>
}
